/**
 * Project operations service
 *
 * Manages project creation, listing, joining, and access control.
 */

import * as Sentry from '@sentry/nextjs'
import type { SupabaseClient } from '@supabase/supabase-js'
import { supabase } from './supabase'
import { readAllPages } from './supabasePaging'
import { fetchProfilePresence, type ProfilePresence } from './profilePresence'
import { authenticatedRequest } from './authenticatedRequest'
import { getApiUrl } from './serverConfig'
import { trackProjectDeleted } from './analytics'
import { captureSentryError } from '@/lib/sentry'
import {
  bulkSetProjectOrganizationViaBackend,
  bulkSetTeamProjectOrganizationViaBackend,
  claimQuickStartProjectViaBackend,
  createFolderViaBackend,
  createProjectViaBackend,
  createTeamFolderViaBackend,
  deepCopyProjectRows,
  deleteFolderViaBackend,
  deleteTeamFolderViaBackend,
  joinProjectViaBackend,
  releaseQuickStartProjectViaBackend,
  setProjectOrganizationViaBackend,
  setProjectVisibilityViaBackend,
  updateFolderViaBackend,
  updateTeamFolderViaBackend,
} from './api'

import type { ProjectRole, ProjectVisibility } from './projectRoles'

export type { ProjectRole, ProjectVisibility }

export interface Project {
  id: string
  name: string
  createdAt: string
  /** @deprecated Use visibility instead. Will be removed in future version. */
  isLocked: boolean
  /** Project visibility setting */
  visibility: ProjectVisibility
  /** Team that owns this project (null = personal project) */
  teamId: string | null
  /** Throwaway snapshot preview project (spec/46) — opened read-only; kept or discarded. */
  isPreview?: boolean
}

export interface ProjectWithRole extends Project {
  role: ProjectRole
  joinedAt: string
  /** Last time the current user opened this project (null = never since tracking began). */
  lastOpenedAt: string | null
  /**
   * Per-user folder this project is filed under on the projects page (ENG-245).
   * null/undefined = ungrouped. Only populated by listMyProjects.
   */
  folderId?: string | null
  /** Per-user fractional sort position within its folder/bucket (ENG-245). */
  sortPosition?: number | null
}

/**
 * A per-user folder for organizing the projects list (ENG-245).
 */
export interface ProjectFolder {
  id: string
  name: string
  position: number
  createdAt: string
}

type ProjectMembershipRow = {
  role: string
  joined_at: string | null
  projects: {
    id: string
    name: string
    created_at: string | null
    deleted_at: string | null
    is_locked: boolean
    visibility: ProjectVisibility | null
    team_id: string | null
    is_preview?: boolean | null
  }
}

type ProjectActivityRow = {
  project_id: string
  last_opened_at: string | null
}

type ProjectOrganizationRow = {
  project_id: string
  folder_id: string | null
  sort_position: number | null
}

/**
 * Build the project_members select. Both newer columns are gated so we can
 * gracefully fall back when a deployed DB hasn't been migrated yet:
 * - is_preview (spec/46) on the joined projects row
 */
function buildMembershipSelect(includePreviewColumn: boolean): string {
  const previewColumn = includePreviewColumn ? ', is_preview' : ''
  return `
  project_id,
  role,
  joined_at,
  projects!inner(id, name, created_at, deleted_at, is_locked, visibility, team_id${previewColumn})
`
}

function isMissingColumnError(
  error: { code?: string; message?: string; details?: string; hint?: string },
  column: string,
): boolean {
  const text = `${error.code ?? ''} ${error.message ?? ''} ${error.details ?? ''} ${error.hint ?? ''}`.toLowerCase()
  return (
    text.includes(column) &&
    (text.includes('does not exist') || text.includes('could not find') || text.includes('schema cache'))
  )
}

function isMissingRelationError(
  error: { code?: string; message?: string; details?: string; hint?: string },
  relation: string,
): boolean {
  const text = `${error.code ?? ''} ${error.message ?? ''} ${error.details ?? ''} ${error.hint ?? ''}`.toLowerCase()
  return text.includes(relation) && (text.includes('does not exist') || text.includes('schema cache'))
}

export function isMissingProjectPreviewColumnError(error: {
  code?: string
  message?: string
  details?: string
  hint?: string
}): boolean {
  return isMissingColumnError(error, 'is_preview')
}

/** projects.kind (system projects hidden from lists) is newer than is_preview; same degrade path on an un-migrated DB. */
export function isMissingProjectKindColumnError(error: {
  code?: string
  message?: string
  details?: string
  hint?: string
}): boolean {
  return isMissingColumnError(error, 'kind')
}

/**
 * Max ids per `.in('project_id', …)` read. The filter rides in the URL, and the
 * Supabase gateway answers 400 once the query string passes ~25k characters
 * (~650 UUIDs) — an account with 722 projects lost its whole project list to
 * it. 200 ids keep each request near 8k characters.
 */
const PROJECT_ID_FILTER_CHUNK_SIZE = 200

function chunkIds(ids: string[], size: number = PROJECT_ID_FILTER_CHUNK_SIZE): string[][] {
  const chunks: string[][] = []
  for (let i = 0; i < ids.length; i += size) chunks.push(ids.slice(i, i + size))
  return chunks
}

/**
 * The first batch error that is not `relation` missing. Callers check every
 * batch for one before degrading on a missing relation, so a migration lag in
 * one batch can never hide a real failure in another.
 */
function firstRealBatchError(
  responses: Array<{ error: { code?: string; message?: string; details?: string; hint?: string } | null }>,
  relation: string,
): { message?: string } | null {
  return responses.find(({ error }) => error && !isMissingRelationError(error, relation))?.error ?? null
}

async function fetchProjectActivityById(
  userId: string,
  projectIds: string[],
): Promise<Map<string, ProjectActivityRow['last_opened_at']>> {
  const result = new Map<string, ProjectActivityRow['last_opened_at']>()
  if (projectIds.length === 0) return result

  const responses = await Promise.all(
    chunkIds(projectIds).map(ids =>
      supabase
        .from('project_member_activity')
        .select('project_id, last_opened_at')
        .eq('user_id', userId)
        .in('project_id', ids),
    ),
  )

  const failure = firstRealBatchError(responses, 'project_member_activity')
  if (failure) throw new Error(`Failed to list project activity: ${failure.message}`)
  // Allows deploys where frontend code reaches an environment before the new
  // privacy table migration has landed; recency simply falls back to joined_at.
  if (responses.some(({ error }) => error)) return new Map()

  for (const { data } of responses) {
    for (const row of (data as unknown as ProjectActivityRow[] | null) ?? []) {
      result.set(row.project_id, row.last_opened_at)
    }
  }
  return result
}

async function fetchProjectOrganizationById(
  userId: string,
  projectIds: string[],
): Promise<Map<string, { folderId: string | null; sortPosition: number | null }>> {
  const result = new Map<string, { folderId: string | null; sortPosition: number | null }>()
  if (projectIds.length === 0) return result

  const untyped = supabase as unknown as SupabaseClient
  const responses = await Promise.all(
    chunkIds(projectIds).map(ids =>
      untyped
        .from('project_member_organization')
        .select('project_id, folder_id, sort_position')
        .eq('user_id', userId)
        .in('project_id', ids),
    ),
  )

  const failure = firstRealBatchError(responses, 'project_member_organization')
  if (failure) throw new Error(`Failed to list project organization: ${failure.message}`)
  // Allows partial deploys before the private organization table migration has landed.
  if (responses.some(({ error }) => error)) return new Map()

  for (const { data } of responses) {
    for (const row of (data as unknown as ProjectOrganizationRow[] | null) ?? []) {
      result.set(row.project_id, { folderId: row.folder_id ?? null, sortPosition: row.sort_position ?? null })
    }
  }
  return result
}

// ============================================================================
// Session-scoped project tracking for transfer on sign-in
// ============================================================================

const SESSION_PROJECTS_KEY = 'mySessionProjects'

/**
 * Track a project created in this session.
 * Used for transferring projects when signing into existing account.
 */
export function trackProjectInSession(projectId: string): void {
  if (typeof sessionStorage === 'undefined') return
  const projects = JSON.parse(sessionStorage.getItem(SESSION_PROJECTS_KEY) || '[]')
  if (!projects.includes(projectId)) {
    projects.push(projectId)
    sessionStorage.setItem(SESSION_PROJECTS_KEY, JSON.stringify(projects))
  }
}

// ============================================================================
// Project CRUD Operations
// ============================================================================

/**
 * Create a new project and add creator as owner
 */
export async function createProject(name?: string): Promise<Project> {
  const result = await createProjectViaBackend({ name })

  // Track for session-scoped transfer
  trackProjectInSession(result.id)

  const visibility = (result.visibility as ProjectVisibility) || 'link_edit'
  return {
    id: result.id,
    name: result.name,
    createdAt: result.createdAt,
    isLocked: visibility === 'private',
    visibility,
    teamId: result.teamId,
  }
}

/**
 * Create a new project assigned to a team.
 * Sets team_id, billing_org_id, billing_mode, and visibility appropriately.
 */
export async function createTeamProject(teamId: string, name?: string): Promise<Project> {
  const result = await createProjectViaBackend({ name, teamId })

  trackProjectInSession(result.id)

  const visibility = (result.visibility as ProjectVisibility) || 'private'
  return {
    id: result.id,
    name: result.name,
    createdAt: result.createdAt,
    isLocked: visibility === 'private',
    visibility,
    teamId: result.teamId,
  }
}

/**
 * Read projects.is_preview (spec/46). The column is newer than the committed
 * supabase types, and the local DB carries unrelated drift so we can't safely
 * regenerate them — read it through an untyped select escape instead.
 */
async function fetchIsPreview(projectId: string): Promise<boolean> {
  const previewSelect: string = 'is_preview'
  const { data } = await supabase.from('projects').select(previewSelect).eq('id', projectId).maybeSingle()
  return (data as unknown as { is_preview?: boolean } | null)?.is_preview ?? false
}

/**
 * Get all projects the current user is a member of
 */
export async function listMyProjects(opts?: {
  orderBy?: 'joined' | 'recent'
  limit?: number
}): Promise<ProjectWithRole[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return []

  const queryMemberships = (includePreviewColumn: boolean) => {
    let query = supabase
      .from('project_members')
      .select(buildMembershipSelect(includePreviewColumn))
      .eq('user_id', user.id)
      .is('projects.deleted_at', null)

    if (includePreviewColumn) {
      query = query.neq('projects.is_preview', true)
      // System projects ("API Generations", "Workflow runs") are API plumbing,
      // reached by link only. .filter keeps the column untyped, like the
      // projects page does for is_preview.
      query = query.filter('projects.kind', 'eq', 'user')
    }

    return query
  }
  // A short list asks for its rows; the full list walks every page, or a member
  // of more than a thousand projects would silently lose the rest.
  const readMemberships = (includePreviewColumn: boolean) =>
    opts?.limit != null && opts.orderBy !== 'recent'
      ? queryMemberships(includePreviewColumn).order('joined_at', { ascending: false }).limit(opts.limit)
      : readAllPages(() => queryMemberships(includePreviewColumn), { key: 'project_id', newestFirst: 'joined_at' })

  // Strip newer columns on a missing-column error so the page still degrades to
  // the legacy list on an un-migrated DB.
  let result = await readMemberships(true)
  if (
    result.error &&
    (isMissingProjectPreviewColumnError(result.error) || isMissingProjectKindColumnError(result.error))
  ) {
    result = await readMemberships(false)
  }
  const { data, error } = result

  if (error) {
    throw new Error(`Failed to list projects: ${error.message}`)
  }

  if (!data) {
    return []
  }

  const rows = data as unknown as ProjectMembershipRow[]
  const projectIds = rows.map(row => row.projects.id)
  const [activityByProjectId, organizationByProjectId] = await Promise.all([
    fetchProjectActivityById(user.id, projectIds),
    fetchProjectOrganizationById(user.id, projectIds),
  ])

  let projects = rows.map(row => {
    // Support both old is_locked and new visibility fields
    const visibility =
      (row.projects.visibility as ProjectVisibility) || (row.projects.is_locked ? 'private' : 'link_edit')
    return {
      id: row.projects.id,
      name: row.projects.name,
      createdAt: row.projects.created_at!,
      isLocked: visibility === 'private',
      visibility,
      teamId: row.projects.team_id,
      isPreview: row.projects.is_preview ?? false,
      role: row.role as ProjectRole,
      joinedAt: row.joined_at!,
      lastOpenedAt: activityByProjectId.get(row.projects.id) ?? null,
      folderId: organizationByProjectId.get(row.projects.id)?.folderId ?? null,
      sortPosition: organizationByProjectId.get(row.projects.id)?.sortPosition ?? null,
    }
  })

  if (opts?.orderBy === 'recent') {
    const toTimestamp = (value: string | null): number => {
      const timestamp = Date.parse(value ?? '')
      return Number.isFinite(timestamp) ? timestamp : 0
    }
    projects = projects.sort((a, b) => {
      const aTime = toTimestamp(a.lastOpenedAt ?? a.joinedAt)
      const bTime = toTimestamp(b.lastOpenedAt ?? b.joinedAt)
      return bTime - aTime
    })
    if (opts.limit != null) projects = projects.slice(0, opts.limit)
  }

  return projects
}

/**
 * Get all projects visible to the current user in a team workspace.
 */
export async function listTeamProjects(teamId: string): Promise<Array<Pick<Project, 'id' | 'name' | 'teamId'>>> {
  const queryProjects = (hideSystemProjects: boolean) => {
    let query = supabase
      .from('projects')
      .select('id, name, team_id, created_at')
      .eq('team_id', teamId)
      .is('deleted_at', null)
    // System projects are API plumbing, reached by link only.
    if (hideSystemProjects) query = query.filter('kind', 'eq', 'user')
    return query
  }
  // Every page: a team past a thousand projects would otherwise silently lose the rest.
  const paging = { key: 'id', newestFirst: 'created_at' }
  let result = await readAllPages(() => queryProjects(true), paging)
  if (result.error && isMissingProjectKindColumnError(result.error)) {
    result = await readAllPages(() => queryProjects(false), paging)
  }
  const { data, error } = result

  if (error) {
    throw new Error(`Failed to list team projects: ${error.message}`)
  }

  return (data ?? []).map(project => ({
    id: project.id,
    name: project.name,
    teamId: project.team_id,
  }))
}

/**
 * Most-recently-opened projects for the current user, newest first. Backs the
 * "Open Recent" menu. Falls back to join time for projects never opened since
 * recency tracking began, so the list is always populated.
 */
export async function listRecentProjects(limit = 8): Promise<ProjectWithRole[]> {
  return listMyProjects({ orderBy: 'recent', limit })
}

// ============================================================================
// Project folders (ENG-245) — per-user organization of the projects list
// ============================================================================

/**
 * List the current user's project folders, ordered by position then creation.
 * Degrades to [] if the folders table hasn't been migrated yet, so the projects
 * page still renders for everyone during a partial rollout.
 */
export async function listMyFolders(): Promise<ProjectFolder[]> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return []

  // project_folders isn't in the generated Database types yet (see migration),
  // mirroring the untyped-select escape used elsewhere in this file. Query it
  // through an untyped client view.
  const untyped = supabase as unknown as SupabaseClient
  const { data, error } = await untyped
    .from('project_folders')
    .select('id, name, position, created_at')
    .eq('user_id', user.id)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true })

  if (error) {
    // Table not migrated yet: degrade to "no folders" rather than breaking the page.
    if (isMissingColumnError(error, 'project_folders')) return []
    throw new Error(`Failed to list folders: ${error.message}`)
  }

  const rows = (data ?? []) as Array<{ id: string; name: string; position: number; created_at: string }>
  return rows.map(row => ({
    id: row.id,
    name: row.name,
    position: row.position,
    createdAt: row.created_at,
  }))
}

/** Create a new folder for the current user. */
export async function createFolder(name: string): Promise<ProjectFolder> {
  const folder = await createFolderViaBackend(name)
  return { id: folder.id, name: folder.name, position: folder.position, createdAt: folder.createdAt }
}

/** Rename and/or reposition a folder. */
export async function updateFolder(
  folderId: string,
  updates: { name?: string; position?: number },
): Promise<ProjectFolder> {
  const folder = await updateFolderViaBackend(folderId, updates)
  return { id: folder.id, name: folder.name, position: folder.position, createdAt: folder.createdAt }
}

/** Delete a folder; its projects fall back to ungrouped. */
export async function deleteFolder(folderId: string): Promise<void> {
  await deleteFolderViaBackend(folderId)
}

/**
 * Move a project into a folder and/or set its sort position for the current
 * user. folderId null = ungrouped; omit sortPosition to append to the end.
 */
export async function setProjectOrganization(
  projectId: string,
  organization: { folderId: string | null; sortPosition?: number | null },
): Promise<{ folderId: string | null; sortPosition: number | null }> {
  return setProjectOrganizationViaBackend(projectId, organization)
}

/**
 * Items per bulk reorder request: the backend's `ORGANIZATION_BULK_MAX`. A drop
 * renumbers its whole bucket, and a bucket can hold more than one request takes.
 */
export const REORDER_BATCH = 1000

/**
 * Bulk-assign folder + sort position for several projects (drives drag-reorder,
 * which renumbers a whole bucket on drop). Sent in order, in batches the
 * endpoint accepts; like the endpoint itself it is not transactional, so a
 * failed batch throws and the caller reloads to reconcile.
 */
export async function reorderProjects(
  items: Array<{ projectId: string; folderId: string | null; sortPosition: number }>,
): Promise<void> {
  for (let start = 0; start < items.length; start += REORDER_BATCH) {
    await bulkSetProjectOrganizationViaBackend(items.slice(start, start + REORDER_BATCH))
  }
}

// ============================================================================
// Team project organization: SHARED per-team folders + reordering (ENG-245)
// ============================================================================
// Unlike personal folders, these belong to the team: every member sees the same
// folders and the same order. Reads go straight to the RLS-gated tables; writes
// go through the backend (which checks org membership + the internal gate).

/** A shared per-team folder (same shape as a personal folder, plus its team). */
export interface TeamProjectFolder extends ProjectFolder {
  teamId: string
}

/**
 * List shared folders for the given teams, ordered by position then creation.
 * Degrades to [] if the table hasn't been migrated yet, so the projects page
 * still renders for everyone during a partial rollout.
 */
export async function listTeamFolders(teamIds: string[]): Promise<TeamProjectFolder[]> {
  if (teamIds.length === 0) return []

  // team_project_folders isn't in the generated Database types (same untyped
  // escape as listMyFolders); query it through an untyped client view.
  const untyped = supabase as unknown as SupabaseClient
  const { data, error } = await untyped
    .from('team_project_folders')
    .select('id, team_id, name, position, created_at')
    .in('team_id', teamIds)
    .order('position', { ascending: true })
    .order('created_at', { ascending: true })

  if (error) {
    if (isMissingRelationError(error, 'team_project_folders')) return []
    throw new Error(`Failed to list team folders: ${error.message}`)
  }

  const rows = (data ?? []) as Array<{
    id: string
    team_id: string
    name: string
    position: number
    created_at: string
  }>
  return rows.map(row => ({
    id: row.id,
    teamId: row.team_id,
    name: row.name,
    position: row.position,
    createdAt: row.created_at,
  }))
}

/**
 * Fetch the shared folder assignment + sort position for the given team
 * projects, keyed by project id. Degrades to an empty map if unmigrated.
 */
export async function listTeamProjectOrganization(
  projectIds: string[],
): Promise<Map<string, { folderId: string | null; sortPosition: number | null }>> {
  const result = new Map<string, { folderId: string | null; sortPosition: number | null }>()
  if (projectIds.length === 0) return result

  const untyped = supabase as unknown as SupabaseClient
  const responses = await Promise.all(
    chunkIds(projectIds).map(ids =>
      untyped.from('team_project_organization').select('project_id, folder_id, sort_position').in('project_id', ids),
    ),
  )

  const failure = firstRealBatchError(responses, 'team_project_organization')
  if (failure) throw new Error(`Failed to list team project organization: ${failure.message}`)
  if (responses.some(({ error }) => error)) return new Map()

  for (const { data } of responses) {
    for (const row of (data ?? []) as Array<{
      project_id: string
      folder_id: string | null
      sort_position: number | null
    }>) {
      result.set(row.project_id, { folderId: row.folder_id ?? null, sortPosition: row.sort_position ?? null })
    }
  }
  return result
}

/** Create a shared folder for a team. */
export async function createTeamFolder(teamId: string, name: string): Promise<TeamProjectFolder> {
  const folder = await createTeamFolderViaBackend(teamId, name)
  return { id: folder.id, teamId, name: folder.name, position: folder.position, createdAt: folder.createdAt }
}

/** Rename and/or reposition a shared team folder. */
export async function updateTeamFolder(
  teamId: string,
  folderId: string,
  updates: { name?: string; position?: number },
): Promise<TeamProjectFolder> {
  const folder = await updateTeamFolderViaBackend(teamId, folderId, updates)
  return { id: folder.id, teamId, name: folder.name, position: folder.position, createdAt: folder.createdAt }
}

/** Delete a shared team folder; its projects fall back to ungrouped. */
export async function deleteTeamFolder(teamId: string, folderId: string): Promise<void> {
  await deleteTeamFolderViaBackend(teamId, folderId)
}

/**
 * Bulk-assign folder + sort position for several of a team's projects (drives
 * drag-reorder, which renumbers a whole bucket on drop). Shared across the team.
 * Batched like `reorderProjects`.
 */
export async function reorderTeamProjects(
  teamId: string,
  items: Array<{ projectId: string; folderId: string | null; sortPosition: number }>,
): Promise<void> {
  for (let start = 0; start < items.length; start += REORDER_BATCH) {
    await bulkSetTeamProjectOrganizationViaBackend(teamId, items.slice(start, start + REORDER_BATCH))
  }
}

/**
 * Record that the current user just opened a project so it sorts to the top of
 * "Open Recent". Best-effort and fire-and-forget — never throws, so it can't
 * disrupt the open flow.
 *
 * `device` is an additive UA hint forwarded in the request body: the backend
 * fires the `opened_project_desktop` Loops hook (and mirrors the
 * `opened_project_desktop_at` contact property the 24h handoff nudge filters
 * on) when it reads 'desktop'. UA-mobile sessions never call this — the
 * mobile wall skips open-marking entirely (honest opens, contract C10).
 */
export async function markProjectOpened(projectId: string, device?: 'mobile' | 'desktop'): Promise<void> {
  try {
    await authenticatedRequest(`${getApiUrl()}/projects/${projectId}/opened`, {
      method: 'POST',
      ...(device ? { body: JSON.stringify({ device }) } : {}),
    })
  } catch {
    // Recency tracking is non-critical; ignore failures.
  }
}

/**
 * Get a single project by ID (if user has access)
 */
export async function getProject(projectId: string): Promise<ProjectWithRole | null> {
  // The session is already local; getUser() would round-trip to the auth server for a
  // user we hold, and the row-level policies below re-check the token anyway.
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const user = session?.user
  if (!user) return null

  const getProjectSelect: string = `
      role,
      joined_at,
      projects!inner(id, name, created_at, deleted_at, is_locked, visibility, team_id)
    `
  // Membership, activity, and preview flag are independent — one round trip instead of three.
  const [{ data: rawData, error }, activityByProjectId, isPreview] = await Promise.all([
    supabase
      .from('project_members')
      .select(getProjectSelect)
      .eq('project_id', projectId)
      .eq('user_id', user.id)
      .is('projects.deleted_at', null)
      .single(),
    fetchProjectActivityById(user.id, [projectId]),
    fetchIsPreview(projectId),
  ])

  const data = rawData as unknown as ProjectMembershipRow | null

  if (!error && data) {
    // Support both old is_locked and new visibility fields
    const visibility =
      (data.projects.visibility as ProjectVisibility) || (data.projects.is_locked ? 'private' : 'link_edit')

    return {
      id: data.projects.id,
      name: data.projects.name,
      createdAt: data.projects.created_at!,
      isLocked: visibility === 'private',
      visibility,
      teamId: data.projects.team_id,
      isPreview,
      role: data.role as ProjectRole,
      joinedAt: data.joined_at!,
      lastOpenedAt: activityByProjectId.get(projectId) ?? null,
    }
  }

  // Not a direct project member — check if this is a team project the user can access.
  // The RLS policy on projects grants SELECT to team members via is_org_member(),
  // so if this query returns a result, the user has team-level access.
  const teamProjectSelect: string = 'id, name, created_at, is_locked, visibility, team_id'
  const { data: rawProject } = await supabase
    .from('projects')
    .select(teamProjectSelect)
    .eq('id', projectId)
    .is('deleted_at', null)
    .single()

  const project = rawProject as unknown as {
    id: string
    name: string
    created_at: string | null
    is_locked: boolean
    visibility: ProjectVisibility | null
    team_id: string | null
  } | null

  if (!project || !project.team_id) return null

  // Team member accessing a team project — auto-join as editor
  const { error: joinError } = await supabase.from('project_members').insert({
    project_id: projectId,
    user_id: user.id,
    role: 'editor',
  })

  if (joinError) return null

  const visibility = (project.visibility as ProjectVisibility) || 'private'
  return {
    id: project.id,
    name: project.name,
    createdAt: project.created_at!,
    isLocked: visibility === 'private',
    visibility,
    teamId: project.team_id,
    role: 'editor' as ProjectRole,
    joinedAt: new Date().toISOString(),
    lastOpenedAt: null,
  }
}

export type JoinProjectError = 'not_found' | 'locked' | 'failed'

export interface JoinProjectResult {
  success: boolean
  project?: ProjectWithRole
  error?: JoinProjectError
}

/**
 * Join a project via link
 * Returns project details if successful, or error type if failed.
 *
 * Role assignment based on visibility:
 * - link_view: join as viewer
 * - link_edit: join as editor
 * - private: cannot join (error)
 */
export async function joinProject(projectId: string): Promise<JoinProjectResult> {
  try {
    const result = await joinProjectViaBackend(projectId)

    if (!result) {
      return { success: false, error: 'not_found' }
    }

    const visibility = (result.visibility as ProjectVisibility) || 'link_edit'

    return {
      success: true,
      project: {
        id: result.id,
        name: result.name,
        createdAt: result.createdAt,
        isLocked: visibility === 'private',
        visibility,
        teamId: result.teamId,
        role: result.role as ProjectRole,
        joinedAt: result.joinedAt,
        lastOpenedAt: null,
      },
    }
  } catch (error) {
    captureSentryError(error, { operation: 'join_project' })
    return { success: false, error: 'failed' }
  }
}

/**
 * Update project name
 */
export async function updateProjectName(projectId: string, newName: string): Promise<void> {
  const response = await authenticatedRequest(`${getApiUrl()}/projects/${encodeURIComponent(projectId)}/name`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ name: newName.trim() }),
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.error || 'Failed to update project name')
  }
}

/**
 * Soft delete a project (sets deleted_at)
 */
export async function deleteProject(projectId: string): Promise<void> {
  const response = await authenticatedRequest(`${getApiUrl()}/projects/${encodeURIComponent(projectId)}`, {
    method: 'DELETE',
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null)
    throw new Error(body?.error || 'Failed to delete project')
  }

  // Track project deleted
  trackProjectDeleted({ projectId })
}

/**
 * Leave a project (remove self from members)
 */
export async function leaveProject(projectId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')

  const { error } = await supabase.from('project_members').delete().eq('project_id', projectId).eq('user_id', user.id)

  if (error) {
    throw new Error(`Failed to leave project: ${error.message}`)
  }
  void nudgeCollabAccessChanged(projectId, [user.id])
}

/**
 * Membership writes above go straight to Supabase, so the backend never sees
 * them — tell the project's collab room to re-verify its live sockets now
 * rather than at its next periodic check. Best-effort: the room re-checks on
 * its own within a minute regardless.
 */
async function nudgeCollabAccessChanged(projectId: string, userIds: string[]): Promise<void> {
  try {
    await authenticatedRequest(`${getApiUrl()}/projects/${encodeURIComponent(projectId)}/collab/access-changed`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ userIds }),
    })
  } catch (error) {
    captureSentryError(error, { operation: 'collab_access_changed_nudge', level: 'warning', extra: { projectId } })
  }
}

export interface ProjectMember {
  userId: string
  alias: string
  color: string
  role: ProjectRole
  joinedAt: string
  isCurrentUser: boolean
}

/**
 * Get members of a project.
 *
 * Rejects when either read fails — an empty roster is a real answer
 * ("nobody else is here"), so a failed request must not be presented as one.
 * Callers decide how to surface it (error state, best-effort skip, retry).
 */
export async function getProjectMembers(projectId: string): Promise<ProjectMember[]> {
  // Only the caller's id is needed to flag `isCurrentUser`; the cached session has it
  // without the auth-server round trip getUser() makes (this runs several times per
  // editor open — comments panel, filter menu, mentions, author lookup).
  const {
    data: { session },
  } = await supabase.auth.getSession()
  const user = session?.user

  const { data, error } = await supabase
    .from('project_members')
    .select('user_id, role, joined_at')
    .eq('project_id', projectId)
    .order('joined_at', { ascending: true })

  if (error) throw error
  const rows = data ?? []

  // profiles is own-row only under RLS; other members' alias/color come from
  // the presence view. A member without a presence row is skipped, matching
  // the inner join this replaced.
  const presence: Map<string, ProfilePresence> = await fetchProfilePresence(rows.map(row => row.user_id))

  const members: ProjectMember[] = []
  for (const row of rows) {
    const profile = presence.get(row.user_id)
    if (!profile) continue
    members.push({
      userId: row.user_id,
      alias: profile.alias,
      color: profile.color,
      role: row.role as ProjectRole,
      joinedAt: row.joined_at!,
      isCurrentUser: row.user_id === user?.id,
    })
  }
  return members
}

/**
 * Remove a member from a project (owner only)
 * Cannot remove yourself
 */
export async function removeProjectMember(
  projectId: string,
  targetUserId: string,
): Promise<{ success: boolean; error?: string }> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return { success: false, error: 'Not authenticated' }

  // Check if current user is owner
  const { data: membership } = await supabase
    .from('project_members')
    .select('role')
    .eq('project_id', projectId)
    .eq('user_id', user.id)
    .single()

  if (!membership || membership.role !== 'owner') {
    return { success: false, error: 'Only owners can remove members' }
  }

  // Cannot remove yourself
  if (targetUserId === user.id) {
    return { success: false, error: 'Cannot remove yourself from project' }
  }

  // Remove the member
  const { error } = await supabase
    .from('project_members')
    .delete()
    .eq('project_id', projectId)
    .eq('user_id', targetUserId)

  if (error) {
    return { success: false, error: error.message }
  }
  void nudgeCollabAccessChanged(projectId, [targetUserId])

  return { success: true }
}

/**
 * Set project visibility (owner only).
 * Uses backend endpoint to bypass the protect_project_sensitive_columns trigger
 * and to enforce plan gating for private projects.
 */
export async function setProjectVisibility(
  projectId: string,
  visibility: ProjectVisibility,
): Promise<{ success: boolean; error?: string; code?: string }> {
  return setProjectVisibilityViaBackend(projectId, visibility)
}

/**
 * Duplicate a project from the project list.
 * Loads source state, creates new project, syncs state to PartyKit.
 */
export async function duplicateProject(
  sourceProjectId: string,
  originalName: string,
  customName?: string,
  teamId?: string | null,
): Promise<{ projectId: string }> {
  // Lazy import to avoid circular dependencies
  const { bootstrapAndCapture, bootstrapForDuplication } = await import('./multiplayer/bootstrap')

  // 1. Bootstrap source project and capture its Yjs state.
  const stateUpdate = await bootstrapAndCapture(sourceProjectId)

  // 2. Create new project in Supabase under the selected destination.
  const newProject = teamId
    ? await createTeamProject(teamId, customName ?? `Copy of ${originalName}`)
    : await createProject(customName ?? `Copy of ${originalName}`)

  // 3. Deep-copy the source's set/character/placement rows into the new project
  //    (with freshly-minted ids) and get the id maps used to remap the ydoc.
  //    This makes the copy a fully independent project that owns its own rows,
  //    rather than silently sharing the source's set/character rows by asset_id
  //    (deep-copy primitive, spec/46).
  const idMaps = await deepCopyProjectRows(newProject.id, sourceProjectId, stateUpdate)

  // 4. Bootstrap new project's Y.Doc, apply captured state, remap ids, and sync.
  await bootstrapForDuplication(newProject.id, stateUpdate, idMaps)

  return { projectId: newProject.id }
}

// ============================================================================
// Template Project for New Users
// ============================================================================

const TEMPLATE_COPY_TIMEOUT_MS = 30000 // 30 seconds — must exceed sum of inner timeouts (bootstrapAndCapture 10s + createProject + bootstrapForDuplication 10s + 2s wait)

/**
 * Claim quick-start project creation slot for this user.
 * Returns false when user already has projects or has already claimed.
 */
export async function claimQuickStartProjectCreation(): Promise<boolean> {
  const result = await claimQuickStartProjectViaBackend()
  return result.canCreate
}

/**
 * Release quick-start creation slot after a failed attempt.
 * Safe to call as best-effort cleanup.
 */
export async function releaseQuickStartProjectCreation(): Promise<void> {
  await releaseQuickStartProjectViaBackend()
}

/**
 * Create a project pre-populated from the template project.
 * Caller is responsible for gating (e.g. only call for first project).
 *
 * If NEXT_PUBLIC_TEMPLATE_PROJECT_ID is not set, falls back to a regular empty project.
 * On template failure: reports to Sentry and throws (no fallback).
 */
export async function createProjectFromSample(name: string): Promise<{ project: Project }> {
  const sampleProjectId = process.env.NEXT_PUBLIC_SAMPLE_PROJECT_ID
  if (!sampleProjectId) {
    throw new Error('Sample project not configured: NEXT_PUBLIC_SAMPLE_PROJECT_ID is not set')
  }

  try {
    const result = await Promise.race([
      duplicateProject(sampleProjectId, name, name),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Sample project copy timeout')), TEMPLATE_COPY_TIMEOUT_MS),
      ),
    ])

    const newProject = await getProject(result.projectId)
    if (!newProject) {
      throw new Error(`Failed to fetch newly created project: ${result.projectId}`)
    }

    return { project: newProject }
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    captureSentryError(error, {
      operation: 'create_project_from_sample',
      tags: { feature: 'sample-copy' },
      extra: { sampleProjectId },
    })
    throw error
  }
}

export async function createProjectFromTemplate(name: string): Promise<{ project: Project; isFromTemplate: boolean }> {
  const templateProjectId = process.env.NEXT_PUBLIC_TEMPLATE_PROJECT_ID
  if (!templateProjectId) {
    Sentry.captureMessage('Template project not configured: NEXT_PUBLIC_TEMPLATE_PROJECT_ID is not set', {
      level: 'warning',
      tags: { feature: 'template-copy' },
    })
    const project = await createProject(name)
    return { project, isFromTemplate: false }
  }

  try {
    // Duplicate the template project with timeout.
    const result = await Promise.race([
      duplicateProject(templateProjectId, name, name),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('Template copy timeout')), TEMPLATE_COPY_TIMEOUT_MS),
      ),
    ])

    // Fetch the created project details
    const newProject = await getProject(result.projectId)
    if (!newProject) {
      throw new Error(`Failed to fetch newly created project: ${result.projectId}`)
    }

    return { project: newProject, isFromTemplate: true }
  } catch (err) {
    const error = err instanceof Error ? err : new Error(String(err))
    captureSentryError(error, {
      operation: 'create_project_from_template',
      level: 'error',
      tags: { feature: 'template-copy' },
      extra: { templateProjectId },
    })

    // Fallback: create an empty project so onboarding isn't blocked
    try {
      const project = await createProject(name)
      Sentry.captureMessage('Template copy failed; fell back to empty project', {
        level: 'warning',
        tags: { feature: 'template-copy', fallback: 'empty-project' },
      })
      return { project, isFromTemplate: false }
    } catch (fallbackErr) {
      captureSentryError(fallbackErr instanceof Error ? fallbackErr : new Error(String(fallbackErr)), {
        operation: 'create_project_from_template_fallback',
        level: 'error',
        tags: { feature: 'template-copy', fallback: 'failed' },
      })
      throw fallbackErr
    }
  }
}
